/*
 * EINGEFROREN durch AP-1 — Änderungen nur per Änderungsantrag in PROGRESS.md.
 * Nachrichtenprotokoll zwischen Main-Thread (UI) und dem Dedicated Worker
 * "can-core". Vollständige Beschreibung inkl. Richtung und Kadenz: CONTRACTS.md §3.
 */

import type { CanBitrate, CanBusStatus, CanFrameInit, PortRef } from '../can/ICanAdapter';
import type { TraceFormat } from '../trace/types';

/** Bei jeder inkompatiblen Änderung erhöhen; `hello` prüft auf Gleichheit. */
export const PROTOCOL_VERSION = 1;

// ---------------------------------------------------------------------------
// 1. Gepacktes Rx-Batch-Format (Transferable, zero-copy)
// ---------------------------------------------------------------------------

/** Bytes je Frame-Datensatz im `rx-batch`-ArrayBuffer. */
export const FRAME_RECORD_SIZE = 32;

/** Byte-Offsets innerhalb eines Datensatzes (Little Endian, siehe CONTRACTS.md §3.2). */
export const FRAME_OFFSET = {
  timestamp_s: 0, // float64
  id: 8, // uint32
  seq: 12, // uint32
  data: 16, // uint8[8]
  dlc: 24, // uint8
  flags: 25, // uint8
  reserved0: 26, // uint16
  reserved1: 28, // uint32
} as const;

/** Bitmaske für das `flags`-Byte. */
export const FRAME_FLAG = {
  extended: 1 << 0,
  remote: 1 << 1,
  /** Frame wurde von uns gesendet (Tx-Echo / Simulation Mode). */
  tx: 1 << 2,
  /** Dekoder hat den Frame als Fehler markiert (`DecodedFrame::isError`). */
  error: 1 << 3,
  /** Stammt aus der Trace-Wiedergabe, nicht vom Bus. */
  replay: 1 << 4,
} as const;

/**
 * Die drei Dekodierquellen sind UNABHÄNGIG voneinander aktiv (nachgewiesen in
 * `AppendFrame()`): DBC ist aktiv, sobald eine Datei geladen ist; ISOBUS und
 * CANopen hängen an je einem eigenen Menü-Schalter.
 */
export interface DecodeFlags {
  isobusEnabled: boolean;
  canopenEnabled: boolean;
}

/** Herkunft der Spalten "Name"/"Description" für genau diesen Frame. */
export type DecodeSource = 'dbc' | 'fallback' | 'isobus' | 'canopen';

/**
 * Anzeige- und Filterdaten je durchgelassenem Frame; `decoded[i]` gehört zum
 * i-ten Datensatz im gepackten Puffer. `null`, wenn nichts dekodiert wurde.
 */
export interface DecodedInfo {
  /** Spalte "Name". */
  name: string;
  /** Spalte "Description". */
  description: string;
  source: DecodeSource;
  isError: boolean;
  /** z. B. "ISOBUS DM1", "CANopen EMCY" — "" wenn `isError` false. */
  errorCategory: string;
  /** Konkrete Fehlerbeschreibung (= `summary` des Dekoders) für die Checkliste. */
  errorDescription: string;
  /** Für den Doppelklick-Detaildialog; erst bei Bedarf per `getDetail` geholt. */
  hasDetail: boolean;
}

// ---------------------------------------------------------------------------
// 2. Filter-, Statistik- und Katalogstrukturen
// ---------------------------------------------------------------------------

export type ContentFilterMode = 'none' | 'mask';

/** 1:1 zu `PassesFilter()` + `PassesContentFilter()`, siehe CONTRACTS.md §4. */
export interface FilterState {
  show11Bit: boolean;
  show29Bit: boolean;
  contentFilterMode: ContentFilterMode;
  mask: number;
  maskId: number;
  rangeEnabled: boolean;
  rangeMin: number;
  rangeMax: number;
  errorFilterEnabled: boolean;
  errorCategories: string[];
  errorSpecific: string[];
  signalFilterEnabled: boolean;
  signalNames: string[];
}

export const defaultFilterState: FilterState = {
  show11Bit: true,
  show29Bit: true,
  contentFilterMode: 'none',
  mask: 0,
  maskId: 0,
  rangeEnabled: false,
  rangeMin: 0,
  rangeMax: 0x1fffffff,
  errorFilterEnabled: false,
  errorCategories: [],
  errorSpecific: [],
  signalFilterEnabled: false,
  signalNames: [],
};

/** Inhalt der Statusleiste (`UpdateStatsBar`), 1-Sekunden-Kadenz. */
export interface StatsSnapshot {
  rxFrameCount: number;
  txFrameCount: number;
  droppedFrameCount: number;
  /** Aus exakter Bit-Stuffing-Schätzung, 1-s-Fenster. */
  busLoadPercent: number;
  busStatus: CanBusStatus;
  /** Histogramm für das Bus-Statistics-Fenster. */
  errorCategoryCounts: Record<string, number>;
}

/** Separater, schnellerer Abgriff nur für den Buslast-Chart (250-ms-Kadenz). */
export interface BusLoadSample {
  timestamp_s: number;
  percent: number;
}

/** Auswahllisten der Filterdialoge (`knownSignalNames_`, `knownErrorsByCategory_`). */
export interface FilterCatalog {
  signalNames: string[];
  errorsByCategory: Record<string, string[]>;
}

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

// ---------------------------------------------------------------------------
// 3. Kommandos (Main-Thread -> Worker)
// ---------------------------------------------------------------------------

interface CommandBase {
  /** Fortlaufend; der Worker antwortet mit `result` und derselben Id. */
  requestId: number;
}

/** AP-15: `slcan` = Web Serial, `bridge` = lokale WebSocket-Brücke (PEAK/Sontheim). */
export type CanBackend = 'slcan' | 'bridge';

/** Adresse und Token einer `can_bridge`-Instanz. */
export interface BridgeTarget {
  url: string;
  token: string;
}

export type WorkerCommand =
  | (CommandBase & { type: 'hello'; protocolVersion: number })
  | (CommandBase & {
      type: 'open';
      port: PortRef;
      bitrate: CanBitrate;
      dataBitrate?: number;
      channelIndex?: number;
      /** Fehlt = `slcan`, damit bestehende Aufrufe unverändert gelten. */
      backend?: CanBackend;
      /** Pflicht, wenn `backend === 'bridge'`. */
      bridge?: BridgeTarget;
    })
  /**
   * Kandidaten der Brücke. Muss im Worker laufen: `can_bridge` lässt nur EINEN
   * Client zu, ein zweiter Socket aus dem Main-Thread würde die Verbindung des
   * Workers verdrängen.
   */
  | (CommandBase & { type: 'listBridgePorts'; bridge: BridgeTarget })
  | (CommandBase & { type: 'close' })
  | (CommandBase & { type: 'send'; frame: CanFrameInit })
  /** `captureBtn_` (Start/Stop): pausiert nur die Anzeige, nicht den Empfang. */
  | (CommandBase & { type: 'setCapture'; running: boolean })
  | (CommandBase & { type: 'setFilter'; filter: FilterState })
  | (CommandBase & { type: 'setDecodeFlags'; flags: DecodeFlags })
  | (CommandBase & { type: 'loadDbc'; text: string })
  /** Lädt EDS und schaltet CANopen implizit ein (wie `OnLoadEdsClicked`). */
  | (CommandBase & { type: 'loadEds'; text: string })
  /** "Reset Decoding": DBC/EDS verwerfen, Dekoderzustand + Tracking leeren. */
  | (CommandBase & { type: 'resetDecoding' })
  /** Zähler, Buslast, Katalog und Sequenznummer auf 0 ("Clear"). */
  | (CommandBase & { type: 'resetStats' })
  /** Zyklisches Senden (TX Message / Engine Simulator). */
  | (CommandBase & { type: 'txStart'; slotId: number; frame: CanFrameInit; periodMs: number })
  | (CommandBase & { type: 'txStop'; slotId: number })
  /** Detailfelder eines einzelnen Frames für den Doppelklick-Dialog. */
  | (CommandBase & { type: 'getDetail'; seq: number })
  /** Zustandsabzug eines Dekoders für die Views (JSON, siehe CONTRACTS.md §5.3). */
  | (CommandBase & { type: 'getSnapshot'; kind: SnapshotKind; arg?: number })  /**
   * Mitschrieb (AP-11). Der Worker schreibt selbst, weil nur er die Frames VOR
   * den Anzeigefiltern und unabhängig von `setCapture` sieht — genau wie
   * `OnTimerTick()` im Original. Das Handle kommt aus dem Main-Thread, weil nur
   * dort ein Auswahldialog erlaubt ist.
   */
  | (CommandBase & { type: 'recordStart'; handle: FileSystemFileHandle; format: TraceFormat })
  | (CommandBase & { type: 'recordStop' })  /** Wiedergabe einer geladenen Trace-Datei (AP-11). */
  | (CommandBase & { type: 'playbackLoad'; frames: ArrayBuffer })
  | (CommandBase & { type: 'playbackStart'; loop: boolean })
  | (CommandBase & { type: 'playbackStop' })
  /**
   * AP-10: `simulationModeEnabled_`. Solange gesetzt, gehen `send`/`txStart`
   * NICHT an den Adapter, sondern zurueck in den Empfangspfad — nur so
   * erscheinen selbst gesendete Frames in der Tabelle (FM-CON-09, FM-X-05).
   */
  | (CommandBase & { type: 'setSimulationMode'; enabled: boolean })
  /**
   * AP-10: Abonnement der Detailfelder fuer die offenen Detailfenster
   * (FM-WIN-18/19). Leere Liste schaltet die Detailerzeugung wieder ab.
   */
  | (CommandBase & { type: 'setDetailWatch'; watches: DetailWatch[] });

/** Welches Detailfenster mitlesen will. */
export interface DetailWatch {
  kind: 'isobus' | 'canopen';
  /** ISOBUS: PGN. CANopen: COB-ID, also `id & 0x7FF`. */
  key: number;
}

export type SnapshotKind =
  | 'dbc'
  | 'eds'
  | 'isobus-tracked'
  | 'isobus-events'
  | 'dm1'
  | 'address-claim';

/** Warum die Wiedergabe endete — bestimmt den Statustext im Store. */
export type PlaybackEndReason = 'complete' | 'stopped' | 'error';

// ---------------------------------------------------------------------------
// 4. Ereignisse (Worker -> Main-Thread)
// ---------------------------------------------------------------------------

export type WorkerEvent =
  | { type: 'ready'; protocolVersion: number }
  /** Antwort auf jedes Kommando; `value` ist kommandospezifisch. */
  | { type: 'result'; requestId: number; ok: true; value?: unknown }
  | {
      type: 'result';
      requestId: number;
      ok: false;
      error: { code: string; message: string };
    }
  | { type: 'connection'; state: ConnectionState; channel: string; message: string }
  /**
   * Kadenz: ein Batch je Animationsframe (<= 60 Hz), zusätzlich sofort bei
   * >= 512 gepufferten Frames. `frames` wird als Transferable übergeben.
   */
  | { type: 'rx-batch'; frames: ArrayBuffer; count: number; decoded: (DecodedInfo | null)[] }
  | { type: 'stats'; stats: StatsSnapshot }
  | { type: 'bus-load'; sample: BusLoadSample }
  /** Nur wenn sich der Katalog seit dem letzten Mal geändert hat (<= 1 Hz). */
  | { type: 'catalog'; catalog: FilterCatalog }
  /**
   * `reason` und `sentCount` trägt nur das Abschlussereignis; die Statustexte
   * baut daraus der Store, damit der Wortlaut an einer Stelle liegt.
   */
  | {
      type: 'playback';
      running: boolean;
      progress: number;
      sentCount: number;
      reason?: PlaybackEndReason;
    }
  /**
   * Ein Frame, auf den ein `setDetailWatch`-Eintrag passt, samt Detailfeldern.
   * Gepusht statt abgefragt, weil das Original das Detailfenster bei JEDEM
   * passenden Frame neu fuellt und ein zweites `feed()` den Dekoderzustand
   * verfaelschen wuerde.
   */
  | {
      type: 'detail';
      watch: DetailWatch;
      seq: number;
      id: number;
      dlc: number;
      data: number[];
      /** Fenstertitel-Anteil: `pgnName` bzw. `messageTypeName`. */
      title: string;
      summary: string;
      fields: Array<{ label: string; value: string }>;
    }
  /** Unaufgeforderte Fehlermeldung (Leseabbruch, Gerät weg, WASM-Trap). */
  | { type: 'error'; code: string; message: string; fatal: boolean };

/** Detailantwort auf `getDetail` (Wert von `result.value`). */
export interface FrameDetail {
  seq: number;
  id: number;
  fields: Array<{ label: string; value: string }>;
  source: DecodeSource;
}

// ---------------------------------------------------------------------------
// 5. Hilfsfunktionen für das gepackte Format (einzige erlaubte Zugriffsart)
// ---------------------------------------------------------------------------

export function frameCount(buffer: ArrayBuffer): number {
  return Math.floor(buffer.byteLength / FRAME_RECORD_SIZE);
}

export function writeFrameRecord(
  view: DataView,
  index: number,
  frame: {
    id: number;
    dlc: number;
    data: Uint8Array;
    timestamp_s: number;
    seq: number;
    flags: number;
  },
): void {
  const base = index * FRAME_RECORD_SIZE;
  view.setFloat64(base + FRAME_OFFSET.timestamp_s, frame.timestamp_s, true);
  view.setUint32(base + FRAME_OFFSET.id, frame.id >>> 0, true);
  view.setUint32(base + FRAME_OFFSET.seq, frame.seq >>> 0, true);
  const dlc = frame.dlc < 0 ? 0 : frame.dlc > 8 ? 8 : frame.dlc;
  for (let i = 0; i < 8; i += 1) {
    view.setUint8(base + FRAME_OFFSET.data + i, i < dlc ? (frame.data[i] ?? 0) : 0);
  }
  view.setUint8(base + FRAME_OFFSET.dlc, dlc);
  view.setUint8(base + FRAME_OFFSET.flags, frame.flags & 0xff);
  view.setUint16(base + FRAME_OFFSET.reserved0, 0, true);
  view.setUint32(base + FRAME_OFFSET.reserved1, 0, true);
}

export interface UnpackedFrame {
  timestamp_s: number;
  id: number;
  seq: number;
  dlc: number;
  flags: number;
  data: Uint8Array;
}

/** `out.data` zeigt in den Quellpuffer — vor dem Aufheben kopieren. */
export function readFrameRecord(buffer: ArrayBuffer, index: number): UnpackedFrame {
  const view = new DataView(buffer);
  const base = index * FRAME_RECORD_SIZE;
  return {
    timestamp_s: view.getFloat64(base + FRAME_OFFSET.timestamp_s, true),
    id: view.getUint32(base + FRAME_OFFSET.id, true),
    seq: view.getUint32(base + FRAME_OFFSET.seq, true),
    dlc: view.getUint8(base + FRAME_OFFSET.dlc),
    flags: view.getUint8(base + FRAME_OFFSET.flags),
    data: new Uint8Array(buffer, base + FRAME_OFFSET.data, 8),
  };
}
